/**
 * Upload dialog for labor standards — the composition.
 *
 * The routing, parsing, review state and save live in
 * controller/use-recipe-upload; the pure rules in utils/recipe-mapping; the
 * review panels in ui/. This file only lays them out: drop zone, file strip,
 * notices, then the PDF or the spreadsheet review, then the actions.
 */
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/CloudUploadOutlined";
import PdfIcon from "@mui/icons-material/DescriptionOutlined";
import {
	Alert,
	AlertTitle,
	Box,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	Paper,
	Typography,
} from "@mui/material";
import React from "react";

import { GhostButton, MonoText, planner, PrimaryButton, SectionLabel } from "../planner-ui"; // eslint-disable-line sort-imports
import { useRecipeUpload } from "./controller/use-recipe-upload";
import { PdfPhaseMapping, RoleMapping, WorkcenterMapping } from "./ui/mapping-table";
import { PdfBinding, PdfSummary } from "./ui/pdf-summary";
import { PdfOperationsPreview, RecipePreview } from "./ui/preview-table";
import { alertSx, chipMonoSx, panelSx } from "./ui/upload-styles";
import { formatHours, isSpreadsheet } from "./utils/recipe-mapping";

interface UploadLaborStandardDialogProps {
	open: boolean;
	onClose: () => void;
	onUploadSuccess: () => void;
	/** The company's work centres, when the page already has them. */
	workcenters?: string[];
}

const bodyText = { ...planner.type.body, fontFamily: planner.fontUi, color: planner.inkSecondary } as const;
const metaText = { ...planner.type.meta, fontFamily: planner.fontUi, color: planner.inkMuted, display: "block" } as const;

const Reading: React.FC<{ label: string }> = ({ label }) => (
	<Box display="flex" alignItems="center" gap={1}>
		<CircularProgress size={14} sx={{ color: planner.accent }} />
		<Typography sx={bodyText}>{label}</Typography>
	</Box>
);

const WarningList: React.FC<{ warnings: string[] }> = ({ warnings }) => (
	<Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
		{warnings.map((warning) => (
			<Typography component="li" key={warning} sx={bodyText}>
				{warning}
			</Typography>
		))}
	</Box>
);

const UploadLaborStandardDialog: React.FC<UploadLaborStandardDialogProps> = ({ open, onClose, onUploadSuccess, workcenters }) => {
	const upload = useRecipeUpload({ open, onClose, onUploadSuccess, workcenters });
	const { file, fileKind, pdfResult, recipeResult, isJobOrder, dropzone } = upload;
	const { getRootProps, getInputProps, isDragActive } = dropzone;

	return (
		<Dialog
			open={open}
			onClose={upload.handleClose}
			maxWidth="lg"
			fullWidth
			PaperProps={{ sx: { borderRadius: `${planner.radius}px`, boxShadow: planner.shadowDialog, fontFamily: planner.fontUi } }}
		>
			<DialogTitle sx={{ bgcolor: planner.surface }}>
				<Box display="flex" justifyContent="space-between" alignItems="center">
					<Typography sx={{ ...planner.type.pageTitle, color: planner.navy }}>Upload Standards</Typography>
					<IconButton onClick={upload.handleClose} size="small" sx={{ color: planner.inkMuted }}>
						<CloseIcon />
					</IconButton>
				</Box>
			</DialogTitle>

			<DialogContent dividers sx={{ bgcolor: planner.canvas, borderColor: planner.hairline }}>
				{/* Drop Zone */}
				{!file && (
					<Paper
						{...getRootProps()}
						elevation={0}
						sx={{
							p: 4,
							border: `${planner.borderWidth}px dashed ${isDragActive ? planner.accent : planner.hairlineStrong}`,
							borderRadius: `${planner.radius}px`,
							bgcolor: isDragActive ? planner.accentSoft : planner.surface,
							boxShadow: planner.shadowRest,
							cursor: "pointer",
							textAlign: "center",
							mb: 2,
							"&:hover": { borderColor: planner.accent, bgcolor: planner.accentSoft },
						}}
					>
						<input {...getInputProps()} />
						<UploadFileIcon sx={{ fontSize: 40, color: planner.accent, mb: 2 }} />
						<Typography sx={{ ...planner.type.title, fontFamily: planner.fontUi, color: planner.ink, mb: 0.5 }}>
							{isDragActive ? "Drop the file here" : "Drag & drop a spreadsheet, CSV or job-order PDF"}
						</Typography>
						<Typography sx={bodyText}>or click to select a file</Typography>
						<Divider sx={{ my: 2, borderColor: planner.hairline }} />
						<Typography sx={metaText}>
							<Box component="span" sx={{ fontFamily: planner.fontMono }}>
								.xlsx .xls .csv
							</Box>
							{
								" — an SAP master-recipe export (one row per operation and phase, with the resource on each), or a long-format sheet with Product, Recipe group, Batch size, Operation, Work center, Job role, Workers, Duration and Start offset columns. One standard per product and recipe group; each operation keeps its own work center."
							}
						</Typography>
						<Typography sx={{ ...metaText, mt: 0.75 }}>
							<Box component="span" sx={{ fontFamily: planner.fontMono }}>
								.pdf
							</Box>
							{" — a Holman job order is read into operations and phases for you to review before saving"}
						</Typography>
					</Paper>
				)}

				{/* File Info */}
				{file && (
					<Box sx={{ mb: 2 }}>
						<Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
							<Chip icon={fileKind === "pdf" ? <PdfIcon /> : undefined} label={file.name} onDelete={upload.handleClearFile} sx={chipMonoSx} />
							{fileKind === "pdf" ? (
								upload.pdfLoading ? (
									<Reading label="Reading the job order..." />
								) : null
							) : upload.recipeLoading ? (
								<Reading label="Reading the recipe..." />
							) : recipeResult ? (
								<Typography sx={bodyText}>
									{recipeResult.shape === "sap" ? "SAP master recipe" : "Long-format sheet"}
									{recipeResult.sheetName ? ` · ${recipeResult.sheetName}` : ""}
									{` · ${recipeResult.rowsRead} rows read`}
									{` · ${upload.recipeStandards.length} standard${upload.recipeStandards.length === 1 ? "" : "s"}`}
									{recipeResult.zeroHourStepsSkipped > 0 ? ` · ${recipeResult.zeroHourStepsSkipped} zero-hour steps skipped` : ""}
								</Typography>
							) : null}
						</Box>
					</Box>
				)}

				{/* Error Alert */}
				{upload.error && (
					<Alert severity="error" sx={alertSx(planner.unstaffed, planner.unstaffedSoft)}>
						{upload.error}
					</Alert>
				)}

				{/* Success Alert */}
				{upload.successMessage && (
					<Alert severity="success" icon={<CheckCircleIcon />} sx={alertSx(planner.staffed, planner.staffedSoft)}>
						{upload.successMessage}
					</Alert>
				)}

				{/* ---------------- PDF: layout not recognised ---------------- */}
				{pdfResult && !isJobOrder && (
					<Box>
						<Alert severity="warning" sx={alertSx(planner.partial, planner.partialSoft)}>
							<AlertTitle sx={{ ...planner.type.bodyStrong, fontFamily: planner.fontUi }}>This PDF layout was not recognised</AlertTitle>
							We could not read this document as a Holman job order, so nothing was extracted. Nothing has been saved.
						</Alert>
						{pdfResult.warnings.length > 0 && (
							<Paper elevation={0} sx={{ ...panelSx, mb: 2 }}>
								<SectionLabel>What the parser saw</SectionLabel>
								<WarningList warnings={pdfResult.warnings} />
							</Paper>
						)}
						<Paper elevation={0} sx={panelSx}>
							<SectionLabel>Alternative</SectionLabel>
							<Typography sx={{ ...planner.type.body, fontFamily: planner.fontUi, color: planner.ink, mt: 1 }}>
								For free-form documents, use Upload and review on the Work Plan page — it reads layouts this parser does not know, and
								lets you correct what it extracts. Open Work Plan and choose Upload and review, then bring the result back here.
							</Typography>
						</Paper>
					</Box>
				)}

				{/* ---------------- PDF: review before save ---------------- */}
				{pdfResult && isJobOrder && (
					<Box>
						<PdfSummary pdfResult={pdfResult} />

						{/* Totals mismatch - prominent, with both numbers */}
						{!pdfResult.totalsReconcile && (
							<Alert severity="warning" sx={alertSx(planner.partial, planner.partialSoft)}>
								<AlertTitle sx={{ ...planner.type.bodyStrong, fontFamily: planner.fontUi }}>
									Parsed hours do not match the document total
								</AlertTitle>
								The document declares{" "}
								<Box component="span" sx={{ fontFamily: planner.fontMono, fontWeight: 600 }}>
									{pdfResult.declaredTotalHours === null || pdfResult.declaredTotalHours === undefined
										? "no total"
										: formatHours(pdfResult.declaredTotalHours)}
								</Box>
								{", but the operations add up to "}
								<Box component="span" sx={{ fontFamily: planner.fontMono, fontWeight: 600 }}>
									{formatHours(pdfResult.parsedTotalHours)}
								</Box>
								. Check the operations below before saving.
							</Alert>
						)}

						{/* Parser warnings */}
						{pdfResult.warnings.length > 0 && (
							<Alert severity="warning" sx={alertSx(planner.partial, planner.partialSoft)}>
								<AlertTitle sx={{ ...planner.type.bodyStrong, fontFamily: planner.fontUi }}>
									{pdfResult.warnings.length === 1 ? "1 thing to check" : `${pdfResult.warnings.length} things to check`}
								</AlertTitle>
								<Box component="ul" sx={{ m: 0, pl: 2.5 }}>
									{pdfResult.warnings.map((warning) => (
										<Typography component="li" key={warning} sx={{ ...planner.type.body, fontFamily: planner.fontUi }}>
											{warning}
										</Typography>
									))}
								</Box>
							</Alert>
						)}

						<PdfBinding
							pdfResult={pdfResult}
							products={upload.products}
							productId={upload.reviewProductId}
							onProductChange={upload.setReviewProductId}
							workcenter={upload.reviewWorkcenter}
							workcenterOptions={upload.workcenterOptions}
							onWorkcenterChange={upload.setReviewWorkcenter}
						/>

						{/* Phases and their job roles — led by what the company's work phases
						    already answer, not by a mapping chore */}
						<PdfPhaseMapping
							distinctPhases={upload.distinctPhases}
							phaseRoles={upload.phaseRoles}
							workPhaseRole={upload.workPhaseRole}
							unmappedCount={upload.unmappedPhases.length}
							autoMappedCount={upload.autoMappedCount}
							allAutoMapped={upload.allAutoMapped}
							jobRoles={upload.jobRoles}
							onChange={upload.setPhaseRole}
						/>

						<PdfOperationsPreview
							operations={pdfResult.operations}
							operationNames={upload.operationNames}
							phaseRoles={upload.phaseRoles}
							onNameChange={upload.setOperationName}
						/>
					</Box>
				)}

				{/* ---------------- Recipe (spreadsheet): review before save ---------------- */}
				{recipeResult && isSpreadsheet(fileKind) && (
					<Box>
						{(recipeResult.unknownWorkstations ?? []).length > 0 && (
							<WorkcenterMapping
								unknownWorkstations={recipeResult.unknownWorkstations ?? []}
								unresolvedCount={upload.unresolvedWorkstations.length}
								workstationMap={upload.workstationMap}
								options={upload.workcenterOptions}
								onChange={upload.setWorkstationMapping}
							/>
						)}

						{upload.workstationsNeedingRole.length > 0 && (
							<RoleMapping
								workstations={upload.workstationsNeedingRole}
								unmappedCount={upload.unmappedRoleWorkstations.length}
								workstationRoles={upload.workstationRoles}
								jobRoles={upload.jobRoles}
								isSap={recipeResult.shape === "sap"}
								onChange={upload.setWorkstationRole}
							/>
						)}

						<RecipePreview
							groups={upload.recipeByProduct}
							standardCount={upload.recipeStandards.length}
							updateCount={upload.recipeUpdateCount}
							resolveWorkstation={upload.resolveWorkstation}
							workstationRoles={upload.workstationRoles}
							jobRoles={upload.jobRoles}
						/>

						{/* What the parser could not place */}
						{(recipeResult.warnings.length > 0 || recipeResult.unmatchedProducts.length > 0) && (
							<Paper elevation={0} sx={{ ...panelSx, mb: 2 }}>
								<SectionLabel>Warnings</SectionLabel>
								{recipeResult.unmatchedProducts.length > 0 && (
									<Typography sx={{ ...bodyText, mt: 1 }}>
										No product matched {recipeResult.unmatchedProducts.length === 1 ? "this key" : "these keys"}; the standards are saved by
										SKU:{" "}
										<Box component="span" sx={{ fontFamily: planner.fontMono }}>
											{recipeResult.unmatchedProducts.join(", ")}
										</Box>
									</Typography>
								)}
								{recipeResult.warnings.length > 0 && <WarningList warnings={recipeResult.warnings} />}
							</Paper>
						)}
					</Box>
				)}
			</DialogContent>

			<DialogActions sx={{ p: 2, bgcolor: planner.surface }}>
				{fileKind === "pdf" && isJobOrder && upload.unmappedPhases.length > 0 && (
					<Typography sx={{ ...planner.type.meta, fontFamily: planner.fontUi, color: planner.partial, mr: "auto" }}>
						Map every phase to a job role to continue.
					</Typography>
				)}
				{isSpreadsheet(fileKind) && recipeResult && !upload.canSaveRecipe && upload.recipeStandards.length > 0 && (
					<Typography sx={{ ...planner.type.meta, fontFamily: planner.fontUi, color: planner.partial, mr: "auto" }}>
						{upload.unresolvedWorkstations.length > 0 ? "Map every work centre to continue." : "Pick a job role for every work centre to continue."}
					</Typography>
				)}
				{upload.uploadStats.failed > 0 && (
					<MonoText sx={{ color: planner.unstaffed, mr: 1 }}>{`${upload.uploadStats.failed} failed`}</MonoText>
				)}
				<GhostButton onClick={upload.handleClose} disabled={upload.uploading}>
					Cancel
				</GhostButton>
				<PrimaryButton
					onClick={upload.startUpload}
					disabled={upload.primaryDisabled}
					startIcon={upload.uploading ? <CircularProgress size={20} sx={{ color: planner.paper }} /> : <UploadFileIcon />}
				>
					{upload.primaryLabel}
				</PrimaryButton>
			</DialogActions>
		</Dialog>
	);
};

export default UploadLaborStandardDialog;
